/**
 * Router.
 *
 * Three gates, in order: no session → Login; session that has not cleared MFA →
 * enrolment; otherwise the app. The enrolment gate mirrors the API, which
 * refuses every other route for a pre-MFA session — the UI must not offer what
 * the server will reject.
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { Layout } from './componentes/layout'
import { Carregando } from './componentes/ui'
import { podeAdministrar, podeGerirSistema, useAuth } from './estado/auth'
import { Mercado } from './paginas/Mercado'
import { EscolaMercado } from './paginas/EscolaMercado'
import { MercadoSa } from './paginas/MercadoSa'
import { EmpresaSa } from './paginas/EmpresaSa'
import { PedidoNovo } from './paginas/PedidoNovo'
import { Usuarios } from './paginas/Usuarios'
import { Perfil } from './paginas/Perfil'
import { Auditoria } from './paginas/Auditoria'
import { Categorias } from './paginas/Categorias'
import { ConfigurarMfa } from './paginas/ConfigurarMfa'
import { Configuracoes } from './paginas/Configuracoes'
import { Login } from './paginas/Login'
import { ListagemCampanhas } from './paginas/campanhas/Listagem'
import { ListagemTemplates } from './paginas/templates/Listagem'
import { EditorTemplate } from './paginas/templates/Editor'
import { DetalheCampanha } from './paginas/campanhas/Detalhe'
import { Instituicoes } from './paginas/Instituicoes'
import { GaleriaLanding } from './paginas/landing/Galeria'
import { EditorLanding } from './paginas/landing/Editor'
import { Empresas } from './paginas/Empresas'
import { Empresa } from './paginas/Empresa'
import { Painel } from './paginas/Painel'
import { Apresentacao } from './paginas/Apresentacao'
import { Pedidos } from './paginas/Pedidos'
import { Prospeccao } from './paginas/Prospeccao'
import { Precos } from './paginas/Precos'

/** Blocks a route the role cannot reach, instead of rendering an empty screen. */
function SomenteGestao({ children }: { children: React.ReactNode }) {
  const { usuario } = useAuth()
  if (!podeGerirSistema(usuario?.papel)) return <Navigate to="/" replace />
  return <>{children}</>
}

function SomenteAdmin({ children }: { children: React.ReactNode }) {
  const { usuario } = useAuth()
  return podeAdministrar(usuario?.papel) ? <>{children}</> : <Navigate to="/" replace />
}

export function App() {
  const { usuario, carregando, precisaCadastrarMfa } = useAuth()
  const local = useLocation()

  // A apresentação institucional é PÚBLICA: abre por link direto, sem login e
  // sem esperar a sessão restaurar — plateia não espera spinner de sessão.
  if (local.pathname === '/apresentacao') return <Apresentacao />

  if (carregando) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Carregando texto="Restaurando sessão…" />
      </div>
    )
  }

  if (!usuario) return <Login />
  if (precisaCadastrarMfa) return <ConfigurarMfa />

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Painel />} />
          <Route path="/apresentacao" element={<SomenteAdmin><Apresentacao /></SomenteAdmin>} />
        <Route path="/prospeccao" element={<SomenteAdmin><Prospeccao /></SomenteAdmin>} />
        <Route path="/landing" element={<SomenteAdmin><GaleriaLanding /></SomenteAdmin>} />
          <Route path="/landing/:idOuSlug" element={<SomenteAdmin><EditorLanding /></SomenteAdmin>} />
        <Route path="/empresas" element={<SomenteAdmin><Empresas /></SomenteAdmin>} />
          <Route path="/empresas/:id" element={<SomenteAdmin><Empresa /></SomenteAdmin>} />
        <Route path="/templates" element={<SomenteAdmin><ListagemTemplates /></SomenteAdmin>} />
          <Route path="/templates/:id" element={<SomenteAdmin><EditorTemplate /></SomenteAdmin>} />
          <Route path="/campanhas" element={<SomenteAdmin><ListagemCampanhas /></SomenteAdmin>} />
          <Route path="/campanhas/:id" element={<SomenteAdmin><DetalheCampanha /></SomenteAdmin>} />
        <Route path="/pedidos" element={<Pedidos />} />
        <Route path="/pedidos/novo" element={<PedidoNovo />} />
        <Route path="/instituicoes" element={<SomenteAdmin><Instituicoes /></SomenteAdmin>} />
        <Route path="/categorias" element={<SomenteAdmin><Categorias /></SomenteAdmin>} />
        <Route path="/precos" element={<SomenteAdmin><Precos /></SomenteAdmin>} />
        <Route path="/configuracoes" element={<SomenteGestao><Configuracoes /></SomenteGestao>} />
        <Route path="/mercado" element={<SomenteAdmin><Mercado /></SomenteAdmin>} />
        <Route path="/mercado/escola/:id" element={<SomenteAdmin><EscolaMercado /></SomenteAdmin>} />
        <Route path="/mercado-sa" element={<SomenteAdmin><MercadoSa /></SomenteAdmin>} />
        <Route path="/mercado-sa/:cnpj" element={<SomenteAdmin><EmpresaSa /></SomenteAdmin>} />
        <Route path="/usuarios" element={<SomenteAdmin><Usuarios /></SomenteAdmin>} />
        <Route path="/perfil" element={<Perfil />} />
        <Route path="/auditoria" element={<SomenteGestao><Auditoria /></SomenteGestao>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  )
}
